import React, { useState, useRef } from 'react';
import Papa from 'papaparse';
import { supabase } from '../lib/supabaseClient';

const BOOKING_HEADERS = ['property', 'guest_name', 'check_in', 'check_out', 'gross_revenue', 'platform_fee', 'platform', 'notes'];
const EXPENSE_HEADERS = ['property', 'title', 'category', 'amount', 'expense_date', 'vendor', 'notes'];

const BOOKING_TEMPLATE = `property,guest_name,check_in,check_out,gross_revenue,platform_fee,platform,notes
It's Happy Hour,Smith Family,2026-04-01,2026-04-07,3200,480,VRBO,Spring break booking`;

const EXPENSE_TEMPLATE = `property,title,category,amount,expense_date,vendor,notes
4400 Windswept,Monthly cleaning,Cleaning,350,2026-04-01,Beach Clean Co,Regular service`;

function matchProperty(name, properties) {
  if (!name) return null;
  const n = name.trim().toLowerCase();
  for (const p of properties) {
    const pn = p.name.toLowerCase();
    if (n === pn) return p;
    // Partial matches
    if (pn.includes('happy hour') && n.includes('happy hour')) return p;
    if (pn.includes('windswept') && n.includes('windswept')) return p;
  }
  return null;
}

function isValidDate(str) {
  if (!str) return false;
  const d = new Date(str.trim() + 'T12:00:00');
  return !isNaN(d.getTime());
}

function downloadCSV(content, filename) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function CSVImport({ type, properties, onImported, onClose }) {
  const [rows, setRows] = useState([]);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef();

  const isBooking = type === 'bookings';
  const headers = isBooking ? BOOKING_HEADERS : EXPENSE_HEADERS;

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const parsed = [];

        results.data.forEach((row, i) => {
          const rowNum = i + 2; // +2 for header row + 0-index
          const rowErrors = [];
          const prop = matchProperty(row.property, properties);

          if (!prop) {
            rowErrors.push(`Unknown property "${row.property || ''}"`);
          }

          if (isBooking) {
            if (!isValidDate(row.check_in)) rowErrors.push('Invalid check-in date');
            if (!isValidDate(row.check_out)) rowErrors.push('Invalid check-out date');
            if (isValidDate(row.check_in) && isValidDate(row.check_out) && row.check_out <= row.check_in) {
              rowErrors.push('Check-out must be after check-in');
            }
          } else {
            if (!row.title?.trim()) rowErrors.push('Title is required');
            if (!row.amount || isNaN(parseFloat(row.amount))) rowErrors.push('Invalid amount');
            if (!isValidDate(row.expense_date)) rowErrors.push('Invalid date');
          }

          parsed.push({
            rowNum,
            raw: row,
            property: prop,
            errors: rowErrors,
            valid: rowErrors.length === 0,
          });
        });

        setRows(parsed);
        setResult(null);
      },
    });
  };

  const handleImport = async () => {
    const validRows = rows.filter(r => r.valid);
    if (validRows.length === 0) return;
    setImporting(true);

    let imported = 0;
    let skipped = 0;

    for (const r of validRows) {
      let payload;
      if (isBooking) {
        payload = {
          property_id: r.property.id,
          guest_name: r.raw.guest_name?.trim() || null,
          check_in: r.raw.check_in.trim(),
          check_out: r.raw.check_out.trim(),
          gross_revenue: r.raw.gross_revenue ? parseFloat(r.raw.gross_revenue) : null,
          platform_fee: r.raw.platform_fee ? parseFloat(r.raw.platform_fee) : 0,
          platform: r.raw.platform?.trim() || null,
          notes: r.raw.notes?.trim() || null,
        };
      } else {
        payload = {
          property_id: r.property.id,
          title: r.raw.title.trim(),
          amount: parseFloat(r.raw.amount),
          category: r.raw.category?.trim() || 'Other',
          expense_date: r.raw.expense_date.trim(),
          vendor: r.raw.vendor?.trim() || null,
          notes: r.raw.notes?.trim() || null,
        };
      }

      const { error } = await supabase.from(isBooking ? 'bookings' : 'expenses').insert(payload);
      if (error) skipped++;
      else imported++;
    }

    setImporting(false);
    setResult({
      imported,
      skipped: skipped + rows.filter(r => !r.valid).length,
      total: rows.length,
    });
  };

  const handleDownloadTemplate = () => {
    const content = isBooking ? BOOKING_TEMPLATE : EXPENSE_TEMPLATE;
    const filename = isBooking ? 'bookings-template.csv' : 'expenses-template.csv';
    downloadCSV(content, filename);
  };

  const validCount = rows.filter(r => r.valid).length;
  const invalidCount = rows.filter(r => !r.valid).length;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal csv-import-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Import {isBooking ? 'Bookings' : 'Expenses'} from CSV</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {result ? (
            <div className="csv-result">
              <div className="csv-result-icon">&#9989;</div>
              <h4>Import Complete</h4>
              <p>
                <strong className="revenue-green">{result.imported}</strong> {isBooking ? 'bookings' : 'expenses'} imported
                {result.skipped > 0 && (
                  <>, <strong className="revenue-red">{result.skipped}</strong> skipped</>
                )}
              </p>
              <button className="btn btn-primary" onClick={() => { onImported(); onClose(); }}>
                Done
              </button>
            </div>
          ) : (
            <>
              <div className="csv-actions-row">
                <button className="btn btn-secondary btn-sm" onClick={handleDownloadTemplate}>
                  Download {isBooking ? 'Bookings' : 'Expenses'} Template
                </button>
                <label className="btn btn-primary btn-sm csv-file-btn">
                  Select CSV File
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFile}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>

              <div className="csv-format-hint">
                <strong>Expected columns:</strong> {headers.join(', ')}
                <br />
                <small>Property names: "It's Happy Hour", "Happy Hour", "4400 Windswept", "Windswept" (case insensitive)</small>
              </div>

              {rows.length > 0 && (
                <>
                  <div className="csv-summary">
                    <span className="revenue-green">{validCount} valid</span>
                    {invalidCount > 0 && <span className="revenue-red">{invalidCount} with errors</span>}
                    <span>of {rows.length} rows</span>
                  </div>

                  <div className="csv-preview-wrapper">
                    <table className="revenue-table csv-preview-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          {headers.map(h => <th key={h}>{h}</th>)}
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r, i) => (
                          <tr key={i} className={r.valid ? '' : 'csv-row-error'}>
                            <td>{r.rowNum}</td>
                            {headers.map(h => (
                              <td key={h}>{r.raw[h] || ''}</td>
                            ))}
                            <td>
                              {r.valid ? (
                                <span className="revenue-green">OK</span>
                              ) : (
                                <span className="revenue-red" title={r.errors.join('; ')}>
                                  {r.errors[0]}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {validCount > 0 && (
                    <button
                      className="btn btn-primary csv-import-btn"
                      onClick={handleImport}
                      disabled={importing}
                    >
                      {importing ? 'Importing...' : `Import ${validCount} valid row${validCount !== 1 ? 's' : ''}`}
                    </button>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
